import { apiSlice } from '../apiSlice';
import {
	ClickPageDTO,
	ClockPageFilter,
} from '../DTO/app-setting/clock.interface';
import { SuccessResponse, Paginated } from '../DTO/common.interface';
import {
	AddRequest,
	AddSingleRequest,
	MeditationPageDTO,
	MeditationPageFilter,
	MeditationSinglePageDTO,
	MeditationSinglePageFilter,
	UnitSeriesDetailResDTO,
	UnitSeriesPublishDTO,
	UnitSeriesSinglePageResDTO,
	UnitSingleDetailResDTO,
	UnitSinglePublishDTO,
} from '../DTO/exercise/meditation.interface';

export const crossoverApiSlice = apiSlice.injectEndpoints({
	endpoints: (builder) => ({
		getCrossoverList: builder.query<
			SuccessResponse<Paginated<MeditationPageDTO>>,
			MeditationPageFilter
		>({
			query: (body) => ({
				url: `/web/exercise/crossover/series/page`,
				method: 'POST',
				body,
			}),
			providesTags: ['CrossoverList'],
		}),
		getCrossoverSortList: builder.query<
			SuccessResponse<MeditationPageDTO>,
			null
		>({
			query: () => ({
				url: `/web/exercise/crossover/series/sort`,
				method: 'GET',
			}),
			providesTags: ['CrossoverSortList'],
		}),
		setCrossoverSort: builder.mutation<
			SuccessResponse<any>,
			{ seriesIdList: string[] }
		>({
			query: (body) => ({
				url: `/web/exercise/crossover/series/sort`,
				method: 'PUT',
				body,
			}),
			invalidatesTags: ['CrossoverList', 'CrossoverSortList'],
		}),
		getCrossoverPublishingList: builder.query<
			SuccessResponse<MeditationPageDTO[]>,
			null
		>({
			query: () => ({
				url: `/web/exercise/crossover/series/published`,
				method: 'GET',
			}),
		}),
		setCrossoverStatus: builder.mutation<
			SuccessResponse<any>,
			{ seriesPublishDTOList: UnitSeriesPublishDTO[] }
		>({
			query: (body) => ({
				url: `/web/exercise/crossover/series/published`,
				method: 'PUT',
				body,
			}),
			invalidatesTags: ['CrossoverList', 'CrossoverDetail'],
		}),
		getCrossoverDetail: builder.query<
			SuccessResponse<UnitSeriesDetailResDTO>,
			string
		>({
			query: (id) => ({
				url: `/web/exercise/crossover/series/${id}`,
				method: 'GET',
			}),
			providesTags: ['CrossoverDetail'],
		}),
		addCrossover: builder.mutation<SuccessResponse<any>, AddRequest>({
			query: (body) => ({
				url: `/web/exercise/crossover`,
				method: 'POST',
				body,
			}),
			invalidatesTags: ['CrossoverList'],
		}),
		updateCrossover: builder.mutation<SuccessResponse<any>, AddRequest>({
			query: (body) => ({
				url: `/web/exercise/crossover/series/${body.id}`,
				method: 'PUT',
				body,
			}),
			invalidatesTags: ['CrossoverDetail'],
		}),
		deleteCrossover: builder.mutation<SuccessResponse<any>, string>({
			query: (id) => ({
				url: `/web/exercise/crossover/series/${id}`,
				method: 'DELETE',
			}),
		}),
		getCrossoverSingleList: builder.query<
			SuccessResponse<Paginated<MeditationSinglePageDTO>>,
			MeditationSinglePageFilter
		>({
			query: (body) => ({
				url: `/web/exercise/crossover/series/${body.id}/page`,
				method: 'POST',
				body,
			}),
			providesTags: ['CrossoverSingleList'],
		}),
		addCrossoverSingle: builder.mutation<
			SuccessResponse<any>,
			AddSingleRequest
		>({
			query: (body) => ({
				url: `/web/exercise/crossover/series/${body.seriesId}`,
				method: 'POST',
				body,
			}),
		}),
		updateCrossoverSingle: builder.mutation<
			SuccessResponse<any>,
			AddSingleRequest
		>({
			query: (body) => ({
				url: `/web/exercise/crossover/single/${body.id}`,
				method: 'PUT',
				body,
			}),
			invalidatesTags: ['CrossoverSingleDetail'],
		}),
		getCrossoverSingleDetail: builder.query<
			SuccessResponse<UnitSingleDetailResDTO>,
			string
		>({
			query: (id) => ({
				url: `/web/exercise/crossover/single/${id}`,
				method: 'GET',
			}),
			providesTags: ['CrossoverSingleDetail'],
		}),
		getCrossoverSingleStatusList: builder.query<
			SuccessResponse<UnitSeriesSinglePageResDTO[]>,
			string
		>({
			query: (id) => ({
				url: `/web/exercise/crossover/series/${id}/published`,
				method: 'GET',
			}),
		}),
		setCrossoverSingleStatus: builder.mutation<
			SuccessResponse<any>,
			{ singlePublishDTOList: UnitSinglePublishDTO[]; id: string }
		>({
			query: (body) => ({
				url: `/web/exercise/crossover/series/${body.id}/published`,
				method: 'PUT',
				body,
			}),
			invalidatesTags: ['CrossoverSingleList', 'CrossoverSingleDetail'],
		}),
		getCrossoverSingleSortList: builder.query<
			SuccessResponse<UnitSeriesSinglePageResDTO[]>,
			string
		>({
			query: (id) => ({
				url: `/web/exercise/crossover/series/${id}/sort`,
				method: 'GET',
			}),
			providesTags: ['CrossoverSingleSortList'],
		}),
		setCrossoverSingleSort: builder.mutation<
			SuccessResponse<any>,
			{ singleIdList: string[]; id: string }
		>({
			query: (body) => ({
				url: `/web/exercise/crossover/series/${body.id}/sort`,
				method: 'PUT',
				body,
			}),
			invalidatesTags: ['CrossoverSingleSortList', 'CrossoverSingleList'],
		}),
		deleteCrossoverSingle: builder.mutation<SuccessResponse<any>, string>({
			query: (id) => ({
				url: `/web/exercise/crossover/single/${id}`,
				method: 'DELETE',
			}),
		}),
		batchUpdateCrossoverHasNew: builder.mutation<
			SuccessResponse<any>,
			{ list: any[] }
		>({
			query: (body) => ({
				url: `/web/exercise/crossover/series/has-new`,
				method: 'PUT',
				body,
			}),
			invalidatesTags: ['CrossoverList'],
		}),
		batchUpdateSingleCrossoverHasNew: builder.mutation<
			SuccessResponse<any>,
			{ id: string; list: any[] }
		>({
			query: (body) => ({
				url: `/web/exercise/crossover/series/${body.id}/has-new`,
				method: 'PUT',
				body,
			}),
			invalidatesTags: ['CrossoverSingleList'],
		}),
	}),
});

export const {
	useGetCrossoverListQuery,
	useGetCrossoverSortListQuery,
	useGetCrossoverPublishingListQuery,
	useSetCrossoverStatusMutation,
	useGetCrossoverDetailQuery,
	useAddCrossoverMutation,
	useUpdateCrossoverMutation,
	useDeleteCrossoverMutation,
	useGetCrossoverSingleListQuery,
	useAddCrossoverSingleMutation,
	useUpdateCrossoverSingleMutation,
	useGetCrossoverSingleDetailQuery,
	useGetCrossoverSingleStatusListQuery,
	useSetCrossoverSingleStatusMutation,
	useSetCrossoverSortMutation,
	useGetCrossoverSingleSortListQuery,
	useSetCrossoverSingleSortMutation,
	useDeleteCrossoverSingleMutation,
	useBatchUpdateCrossoverHasNewMutation,
	useBatchUpdateSingleCrossoverHasNewMutation,
} = crossoverApiSlice;
